import { useCallback, useRef, useState } from "react";

import { cancelOrder } from "./cancellation-service";
import { CancellationReason, CancellationState, UseCancellationReturn } from "./types";

/**
 * Custom hook for managing order cancellation flow.
 *
 * Manages:
 * - Loading state during cancellation request
 * - Error state with recovery option
 *
 * Uses a ref for the loading guard to prevent stale closure issues
 * with rapid double-clicks.
 *
 * @returns UseCancellationReturn object with state and handlers
 */
export const useCancellation = (): UseCancellationReturn => {
  const [state, setState] = useState<CancellationState>({
    isLoading: false,
    error: null,
  });

  const isLoadingRef = useRef(false);

  const handleCancelOrder = useCallback(async (orderId: string, reason: CancellationReason): Promise<boolean> => {
    if (isLoadingRef.current) {
      return false;
    }
    isLoadingRef.current = true;
    setState({
      isLoading: true,
      error: null,
    });

    try {
      const result = await cancelOrder(orderId, reason);

      if (result.success) {
        setState({
          isLoading: false,
          error: null,
        });
        return true;
      } else {
        setState({
          isLoading: false,
          error: result.error || "We couldn't cancel your order. Please try again or contact support.",
        });
        return false;
      }
    } catch (error) {
      setState({
        isLoading: false,
        error: "We couldn't cancel your order. Please try again or contact support.",
      });
      return false;
    } finally {
      isLoadingRef.current = false;
    }
  }, []);

  const resetError = useCallback(() => {
    setState((prev) => ({
      ...prev,
      error: null,
    }));
  }, []);

  return {
    state,
    cancelOrder: handleCancelOrder,
    resetError,
  };
};

export default useCancellation;
